import { getMerchantClient } from '@beatq/sdk-client';
import {
  ApiError,
  getAccessToken,
  isIdentitySessionError,
  mapErrorCode,
  parseAccessTokenClaims,
  readListFromEnvelope,
  readObjectFieldFromEnvelope,
  setAccessToken,
} from '@beatq/shared';

type ApiErr = { error?: { code: string; message: string } };

export function sdkErrorMessage(error: unknown): string {
  const e = error as ApiErr;
  if (e?.error && isIdentitySessionError(e.error.code)) {
    throw new ApiError(e.error.code, e.error.message);
  }
  return e?.error?.message ?? mapErrorCode(e?.error?.code ?? 'ERR-UNKNOWN');
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** 10-03 ERR-AUTH-0062: show attemptsRemaining when the envelope includes it. */
export function readAttemptsRemaining(error: unknown): number | null {
  const visit = (value: unknown, depth = 0): number | null => {
    if (value == null || depth > 5) return null;
    if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, Math.trunc(value));
    if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
    const rec = asRecord(value);
    if (!rec) {
      if (Array.isArray(value)) {
        for (const item of value) {
          const found = visit(item, depth + 1);
          if (found != null) return found;
        }
      }
      return null;
    }
    if (rec.attemptsRemaining != null) {
      const direct = visit(rec.attemptsRemaining, depth + 1);
      if (direct != null) return direct;
    }
    const nested = visit(rec.error, depth + 1) ?? visit(rec.details, depth + 1);
    if (nested != null) return nested;
    if (Array.isArray(rec.details)) {
      for (const item of rec.details) {
        const row = asRecord(item);
        if (row?.field === 'attemptsRemaining') {
          const fromRow = visit(row.value, depth + 1);
          if (fromRow != null) return fromRow;
        }
      }
    }
    return null;
  };
  return visit(error);
}

export function pinSignInErrorMessage(error: unknown): string {
  const base = sdkErrorMessage(error);
  const remaining = readAttemptsRemaining(error);
  if (remaining == null) return base;
  if (remaining === 1) return `${base} 1 attempt remaining.`;
  return `${base} ${remaining} attempts remaining.`;
}

export type StaffMember = {
  id?: string;
  invitationId?: string | null;
  businessId?: string;
  accountId?: string | null;
  roleCode?: string;
  jobTitle?: string | null;
  status?: string;
  invitedAt?: string;
  acceptedAt?: string | null;
  branchIds?: string[];
  displayName?: string | null;
  email?: string | null;
  lastActiveAt?: string | null;
  pinEnabled?: boolean | null;
  inviteOnly?: boolean;
};

type StaffListRow = StaffMember & {
  account?: { id?: string; email?: string | null; displayName?: string | null };
  branchAssignments?: { branchId?: string }[];
};

export function staffListFromEnvelope(envelope: unknown): StaffMember[] {
  const rows = readListFromEnvelope<StaffListRow>(envelope, ['staff', 'items']);
  return rows.map((row) => ({
    ...row,
    accountId: row.accountId ?? row.account?.id ?? null,
    email: row.email ?? row.account?.email ?? null,
    displayName: row.displayName ?? row.account?.displayName ?? null,
    invitationId: row.invitationId ?? null,
    lastActiveAt: row.lastActiveAt ?? null,
    inviteOnly:
      row.inviteOnly === true || (!row.accountId && !row.account?.id && Boolean(row.invitationId)),
    branchIds:
      row.branchIds ??
      row.branchAssignments?.map((b) => b.branchId).filter((id): id is string => Boolean(id)),
  }));
}

export function inviteResultFromEnvelope(envelope: unknown): {
  invitationId?: string;
  membershipId?: string;
  expiresAt?: string;
  effectivePermissions: string[];
} {
  const data = (envelope as { data?: Record<string, unknown> })?.data ?? {};
  const rawPermissions = data.effectivePermissions ?? data.effectivePermissionsPreview;
  return {
    invitationId: typeof data.invitationId === 'string' ? data.invitationId : undefined,
    membershipId: typeof data.membershipId === 'string' ? data.membershipId : undefined,
    expiresAt: typeof data.expiresAt === 'string' ? data.expiresAt : undefined,
    effectivePermissions: Array.isArray(rawPermissions)
      ? rawPermissions.filter((code): code is string => typeof code === 'string')
      : [],
  };
}

export function staffMemberFromEnvelope(envelope: unknown): StaffMember | undefined {
  return readObjectFieldFromEnvelope<StaffMember>(envelope, ['membership', 'staff']);
}

export function findStaffById(staff: StaffMember[], staffId: string): StaffMember | undefined {
  return staff.find((member) => member.id === staffId || member.invitationId === staffId);
}

/** PIN selector: accepted members with an account. Include rows when pinEnabled is unknown. */
export function pinEligibleStaff(staff: StaffMember[]): StaffMember[] {
  return staff.filter(
    (member) => Boolean(member.accountId) && !member.inviteOnly && member.pinEnabled !== false,
  );
}

export function isPendingInvitation(member: StaffMember): boolean {
  return member.inviteOnly === true || member.status === 'INVITED';
}

export function isSelfStaff(member: StaffMember, accountId?: string | null): boolean {
  if (!accountId) return false;
  return member.accountId === accountId || member.id === accountId;
}

export function staffBranchLabel(
  member: StaffMember,
  branches: { id?: string; name?: string }[],
): string {
  const ids = member.branchIds ?? [];
  if (ids.length === 0) return 'All branches';
  return ids.map((id) => branches.find((branch) => branch.id === id)?.name ?? id).join(', ');
}

export function formatStaffInstant(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-GB');
}

export function staffLastActiveLabel(member: StaffMember): string {
  const formatted = formatStaffInstant(member.lastActiveAt);
  return formatted ? `Last active ${formatted}` : 'Last active not recorded';
}

export function staffStatusLabel(member: StaffMember): string {
  if (member.inviteOnly || member.status === 'INVITED') return 'Invited';
  if (member.status === 'ACTIVE' || !member.status) return 'Active';
  return member.status.replace(/_/g, ' ').toLowerCase();
}

export async function listStaff(params?: Record<string, string>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/staff', {
    params: { query: { limit: '100', ...params } as never },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

/** 10-03 device-scoped PIN picker — not GET /staff (ERR-AUTH-0061). */
export async function listAuthStaff() {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/auth/staff');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function inviteStaffMember(body: Record<string, unknown>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/staff/invitations', {
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function updateStaffMember(staffId: string, body: Record<string, unknown>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.PATCH('/merchant/v1/staff/{id}', {
    params: { path: { id: staffId } },
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function resetStaffPin(staffId: string, body: Record<string, unknown>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/staff/{id}/pin', {
    params: { path: { id: staffId } },
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function removeStaffMember(staffId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.DELETE('/merchant/v1/staff/{id}', {
    params: { path: { id: staffId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function cancelStaffInvitation(invitationId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST(
    '/merchant/v1/staff/invitations/{id}/cancel',
    {
      params: { path: { id: invitationId } },
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function resendStaffInvitation(invitationId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST(
    '/merchant/v1/staff/invitations/{id}/resend',
    {
      params: { path: { id: invitationId } },
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function resetStaffMfa(staffId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/staff/{id}/mfa/reset', {
    params: { path: { id: staffId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function signInWithStaffPin(body: {
  staffId: string;
  pin: string;
  deviceSessionId: string;
  branchId: string;
}) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/auth/staff/pin', {
    body: body as never,
  });
  if (!response.ok || error) throw new Error(pinSignInErrorMessage(error));
  const envelope = data as {
    data?: { accessToken?: string; tokens?: { accessToken?: string } };
  };
  const accessToken = envelope?.data?.accessToken ?? envelope?.data?.tokens?.accessToken;
  if (accessToken) setAccessToken(accessToken);
  return data;
}

/** Shared PIN submit for page + floor modal — requires JWT `sid` + `brn` (no DEV_BRANCH_ID fiction). */
export async function submitStaffPinSignIn(input: { staffId: string; pin: string }) {
  const claims = parseAccessTokenClaims(getAccessToken());
  const deviceSessionId = claims.sid;
  const branchId = claims.brn;
  if (!deviceSessionId) {
    throw new Error('Device session missing — sign in with email and password first');
  }
  if (!branchId) {
    throw new Error('Branch context required — select a branch before PIN sign-in');
  }
  if (!input.staffId) {
    throw new Error('Select a staff member');
  }
  return signInWithStaffPin({
    staffId: input.staffId,
    pin: input.pin.trim(),
    deviceSessionId,
    branchId,
  });
}
